import React from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { PasswordProtectedVideo } from './components/PasswordProtectedVideo';

export const DemoPage = () => {
  return (
    <div style={styles.page}>
      <Header />

      <main style={styles.main}>
        {/* Hero Section with Decorative Elements */}
        <section style={styles.heroSection}>
          {/* Decorative background shapes */}
          <div style={styles.decorShape1} />
          <div style={styles.decorShape2} />
          <div style={styles.decorShape3} />

          <div style={styles.heroContent}>
            <div style={styles.heroTag}>Product Demo</div>
            <h1 style={styles.heroTitle}>
              See Real Patients
              <br />
              <span style={styles.heroHighlight}>in action</span>
            </h1>
            <p style={styles.heroSubtitle}>
              See how real patient voices become decisions your team can act on.
            </p>
          </div>
        </section>

        {/* Video Section */}
        <section style={styles.videoSection}>
          <PasswordProtectedVideo
            src="https://kommodo.ai/embed/recordings/n0ZrpvXGcy29oI66g65x?onlyRecording=1"
            title="Real Patients Product Demo"
            description={'Watch how Real Patients transforms patient experience data into actionable insights for healthcare teams.\n\nThis demo shows the core platform features in action.'}
            correctPassword="getrealpatients"
          />
        </section>

        {/* Key Highlights Section */}
        <section style={styles.featuresSection}>
          <div style={styles.featuresContent}>
            <h2 style={styles.featuresTitle}>
              What you'll see in <span style={styles.featureHighlight}>5 minutes</span>
            </h2>
            <div style={styles.featuresGrid}>
              <div style={styles.featureCard}>
                <div style={styles.featureNumber}>01</div>
                <h3 style={styles.featureCardTitle}>Hear Verified Voices</h3>
                <p style={styles.featureCardText}>
                  The Real Patients community covers over 2,500 health conditions. Hear patients directly, in their own words.
                </p>
              </div>
              <div style={styles.featureCard}>
                <div style={styles.featureNumber}>02</div>
                <h3 style={styles.featureCardTitle}>Data for Decisions</h3>
                <p style={styles.featureCardText}>
                  Data that can inform clinical decisions, regulatory submissions, and product development.
                </p>
              </div>
              <div style={styles.featureCard}>
                <div style={styles.featureNumber}>03</div>
                <h3 style={styles.featureCardTitle}>Answers in Minutes</h3>
                <p style={styles.featureCardText}>
                  Query the data yourself and get insights in minutes, not months.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={styles.ctaSection}>
          <h2 style={styles.ctaTitle}>Ready to see what real patient insights can do?</h2>
          <p style={styles.ctaSubtitle}>
            Transform how your team understands the patient experience.
          </p>
          <a
            href="https://meetings.hubspot.com/molly-schreiber"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.ctaButton}
          >
            Schedule a Call
          </a>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#111111',
    lineHeight: 1.8,
  },
  main: {
    flex: 1,
  },

  // Hero Section
  heroSection: {
    position: 'relative',
    overflow: 'hidden',
    paddingTop: 'clamp(80px, 12vw, 140px)',
    paddingRight: '24px',
    paddingBottom: 'clamp(100px, 15vw, 160px)',
    paddingLeft: '24px',
    background: 'linear-gradient(135deg, #F9F7F4 0%, #fff 60%, #FFF5F0 100%)',
    textAlign: 'center',
  },

  decorShape1: {
    position: 'absolute',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    backgroundColor: 'rgba(241, 91, 65, 0.08)',
    top: '10%',
    right: '5%',
    zIndex: 0,
  },
  decorShape2: {
    position: 'absolute',
    width: '150px',
    height: '150px',
    backgroundColor: 'rgba(56, 92, 188, 0.05)',
    top: '50%',
    left: '5%',
    transform: 'rotate(45deg)',
    zIndex: 0,
  },
  decorShape3: {
    position: 'absolute',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    backgroundColor: 'rgba(241, 91, 65, 0.06)',
    bottom: '15%',
    right: '10%',
    zIndex: 0,
  },

  heroContent: {
    position: 'relative',
    zIndex: 1,
    maxWidth: '900px',
    margin: '0 auto',
  },

  heroTag: {
    display: 'inline-block',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#F15B41',
    marginBottom: '24px',
    padding: '8px 16px',
    backgroundColor: 'rgba(241, 91, 65, 0.1)',
    borderRadius: '20px',
  },

  heroTitle: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: 'clamp(52px, 9vw, 80px)',
    fontWeight: '700',
    lineHeight: 1.15,
    margin: '0 0 32px 0',
    color: '#111111',
  },

  heroHighlight: {
    backgroundColor: 'rgba(241, 91, 65, 0.2)',
    padding: '8px 20px',
    display: 'inline-block',
    borderRadius: '8px',
  },

  heroSubtitle: {
    fontSize: 'clamp(16px, 2.5vw, 20px)',
    color: '#4A4A4A',
    margin: '0 0 40px 0',
    lineHeight: '1.7',
    maxWidth: '700px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  // Video Section
  videoSection: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: 'clamp(80px, 12vw, 120px) 24px',
    width: '100%',
    boxSizing: 'border-box',
  },

  // Features Section
  featuresSection: {
    padding: 'clamp(80px, 12vw, 120px) 24px',
    backgroundColor: '#F9F7F4',
    position: 'relative',
  },

  featuresContent: {
    maxWidth: '1200px',
    margin: '0 auto',
  },

  featuresTitle: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: 'clamp(36px, 6vw, 52px)',
    fontWeight: '700',
    lineHeight: 1.25,
    textAlign: 'center',
    margin: '0 0 64px 0',
    color: '#111111',
  },

  featureHighlight: {
    backgroundColor: 'rgba(241, 91, 65, 0.2)',
    padding: '8px 16px',
    borderRadius: '6px',
    display: 'inline-block',
  },

  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '48px',
    maxWidth: '1200px',
    margin: '0 auto',
  },

  featureCard: {
    padding: '40px 32px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #E8E8E8',
    textAlign: 'left',
    transition: 'all 0.3s ease',
    ':hover': {
      borderColor: '#F15B41',
      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    },
  },

  featureNumber: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '48px',
    fontWeight: '700',
    color: '#F15B41',
    marginBottom: '20px',
    lineHeight: '1',
  },

  featureCardTitle: {
    fontSize: '20px',
    fontWeight: '700',
    margin: '0 0 16px 0',
    color: '#111111',
  },

  featureCardText: {
    fontSize: '15px',
    color: '#4A4A4A',
    margin: '0',
    lineHeight: '1.7',
  },

  // CTA Section
  ctaSection: {
    padding: 'clamp(80px, 12vw, 120px) 24px',
    backgroundColor: '#385cbc',
    textAlign: 'center',
    color: '#fff',
  },

  ctaTitle: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: 'clamp(36px, 6vw, 48px)',
    fontWeight: '700',
    margin: '0 0 20px 0',
    lineHeight: 1.2,
  },

  ctaSubtitle: {
    fontSize: '18px',
    margin: '0 0 40px 0',
    lineHeight: '1.6',
    opacity: 0.95,
  },

  ctaButton: {
    display: 'inline-block',
    textDecoration: 'none',
    padding: '16px 48px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#385cbc',
    backgroundColor: '#fff',
    border: '2px solid #fff',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
};
